import { createReadyAttackState, stepAttackStateMachine, } from "./attack-state-machine.js";
import { createAttackArcConfigForPlayer, } from "./attack-targeting.js";
import { calculateLevelFromSpiritPower, calculateSpiritStoneAbsorption, LEVEL_GROWTH_CONFIGS, } from "./growth-config.js";
import { getLevelCombatConfig, normalizeGameplayLevel, } from "./level-config.js";
import { normalizeAngle, normalizePlayerInput, stepPlayerMotion, } from "./movement.js";
import { getStaminaConfigForLevel } from "./stamina-config.js";
const PLAYER_COLLISION_PUSH_STRENGTH = 0.5;
const PLAYER_COLLISION_MAX_CORRECTION = 12;
const PLAYER_COLLISION_LEVEL_MASS_STEP = 0.225;
const PICKUP_ARC_RADIUS_CAP = 260;
const MIN_SPIRIT_STONE_PICKUP_RADIUS = getLevelCombatConfig(1).attackRadius;
const OVERFLOW_SCATTER_START_DISTANCE = 28;
const OVERFLOW_SCATTER_STEP_DISTANCE = 7;
const OVERFLOW_SCATTER_EDGE_MARGIN = 80;
const PICKUP_STAMINA_RESTORE_MIN_RATIO = 0.08;
const PICKUP_STAMINA_RESTORE_MAX_RATIO = 0.45;
const PICKUP_STAMINA_RESTORE_PER_SPIRIT = 0.12;
const EPSILON = 1e-9;
export function createDefaultWorldConfig() {
    return {
        tickRate: 20,
        motion: {
            minSpeed: 80,
            maxSpeed: 80,
            boostSpeed: 140,
            acceleration: 240,
            turnRate: Math.PI,
        },
        arenaRadius: 2880,
        spawnRadius: 120,
        playerRadius: 18,
        spiritStonePickupRadius: 24,
    };
}
export function createWorld(options = {}) {
    const config = mergeWorldConfig(options.config);
    const spiritStones = Object.fromEntries((options.spiritStones ?? []).map((stone) => [stone.id, { ...stone }]));
    return {
        tick: 0,
        lastUpdatedAt: null,
        config,
        players: {},
        spiritStones,
    };
}
export function spawnPlayer(world, options) {
    const facing = normalizeAngle(options.facing ?? 0);
    const selectedLevel = normalizeGameplayLevel(options.level);
    const initialSpiritStoneCount = LEVEL_GROWTH_CONFIGS[selectedLevel].requiredSpiritPower;
    const staminaConfig = getStaminaConfigForLevel(selectedLevel);
    const player = {
        id: options.id,
        name: options.name ?? options.id,
        position: { x: options.position?.x ?? 0, y: options.position?.y ?? 0 },
        velocity: {
            x: Math.cos(facing) * world.config.motion.minSpeed,
            y: Math.sin(facing) * world.config.motion.minSpeed,
        },
        facing,
        targetAngle: facing,
        speed: world.config.motion.minSpeed,
        radius: world.config.playerRadius + (selectedLevel - 1) * 2,
        level: selectedLevel,
        selectedLevel,
        spiritStoneCount: initialSpiritStoneCount,
        stamina: staminaConfig.maxStamina,
        maxStamina: staminaConfig.maxStamina,
        turnBurstActive: false,
        turnBurstRemainingMs: 0,
        turnBurstCooldownMs: 0,
        isAlive: true,
        deathCount: 0,
        boostActive: false,
        lastInputSeq: 0,
        lastClientTime: 0,
        pendingInput: null,
        attack: createReadyAttackState(),
        treasureEffects: {},
        teamId: options.teamId ?? null,
    };
    world.players[player.id] = player;
    return player;
}
/** Sets (or clears) a player's team for team matches. */
export function setPlayerTeam(world, playerId, teamId) {
    const player = world.players[playerId];
    if (!player) {
        return false;
    }
    player.teamId = teamId;
    return true;
}
export function queuePlayerInput(world, playerId, input) {
    const player = world.players[playerId];
    if (!player || !player.isAlive)
        return false;
    const normalized = normalizePlayerInput(input);
    if (!normalized)
        return false;
    player.pendingInput = normalized;
    return true;
}
export function tickWorld(world, now = Date.now()) {
    const deltaSeconds = 1 / world.config.tickRate;
    const deltaMs = deltaSeconds * 1000;
    const events = [];
    for (const player of Object.values(world.players)) {
        if (!player.isAlive) {
            continue;
        }
        stepPlayerMotion(player, getMotionConfigForPlayer(world.config.motion, player.level, player), deltaSeconds);
        constrainPlayerToArena(world, player);
    }
    resolvePlayerCollisions(world);
    for (const player of Object.values(world.players)) {
        if (!player.isAlive) {
            continue;
        }
        collectSpiritStones(world, player, events);
    }
    for (const player of Object.values(world.players)) {
        stepAttackStateMachine(world, player, deltaMs, events);
    }
    world.tick += 1;
    world.lastUpdatedAt = now;
    return { events };
}
export function addSpiritStone(world, stone) {
    world.spiritStones[stone.id] = { ...stone };
}
export function respawnPlayer(world, playerId, position = { x: 0, y: 0 }) {
    const player = world.players[playerId];
    if (!player)
        return null;
    player.position = { x: position?.x ?? 0, y: position?.y ?? 0 };
    player.velocity = { x: world.config.motion.minSpeed, y: 0 };
    player.facing = 0;
    player.targetAngle = 0;
    player.speed = world.config.motion.minSpeed;
    player.level = calculateLevelFromSpiritPower(0);
    const staminaConfig = getStaminaConfigForLevel(player.level);
    player.radius = world.config.playerRadius + (player.level - 1) * 2;
    player.spiritStoneCount = 0;
    player.stamina = staminaConfig.maxStamina;
    player.maxStamina = staminaConfig.maxStamina;
    player.turnBurstActive = false;
    player.turnBurstRemainingMs = 0;
    player.turnBurstCooldownMs = 0;
    player.isAlive = true;
    player.boostActive = false;
    player.pendingInput = null;
    player.lastInputSeq = 0;
    player.lastClientTime = 0;
    player.attack = createReadyAttackState();
    player.treasureEffects = player.treasureEffects ?? {};
    return player;
}
export function removePlayer(world, playerId) {
    delete world.players[playerId];
}
function mergeWorldConfig(overrides = {}) {
    const base = createDefaultWorldConfig();
    return {
        ...base,
        ...overrides,
        motion: {
            ...base.motion,
            ...(overrides.motion ?? {}),
        },
    };
}
function getMotionConfigForPlayer(base, level, player) {
    const levelConfig = getLevelCombatConfig(level);
    const effects = player?.treasureEffects ?? {};
    return {
        ...base,
        minSpeed: Math.min(base.minSpeed, levelConfig.minSpeed),
        maxSpeed: applyPositiveMultiplier(levelConfig.maxSpeed, effects.speedMultiplier),
        boostSpeed: applyPositiveMultiplier(levelConfig.boostSpeed, effects.boostSpeedMultiplier),
        acceleration: applyPositiveMultiplier(base.acceleration, effects.accelerationMultiplier),
        turnRate: applyPositiveMultiplier(levelConfig.turnRate, effects.turnRateMultiplier),
    };
}
function applyPositiveMultiplier(value, multiplier) {
    if (!Number.isFinite(multiplier) ||
        multiplier === undefined ||
        multiplier <= 0) {
        return value;
    }
    return value * multiplier;
}
function collectSpiritStones(world, player, events) {
    const pickupArc = createSpiritStonePickupArc(player);
    for (const stone of Object.values(world.spiritStones)) {
        if (!isPositionInSpiritStonePickupRange(player, stone.position, pickupArc)) {
            continue;
        }
        delete world.spiritStones[stone.id];
        const effectiveStoneValue = applySpiritAbsorptionMultiplier(player, stone);
        const absorption = calculateSpiritStoneAbsorption(player.spiritStoneCount, effectiveStoneValue);
        player.spiritStoneCount += absorption.absorbedValue;
        player.level = calculateLevelFromSpiritPower(player.spiritStoneCount);
        syncPlayerStaminaForLevel(player);
        restorePlayerStaminaFromSpiritStone(player, absorption.absorbedValue);
        player.radius = world.config.playerRadius + (player.level - 1) * 2;
        events.push({
            type: "pickup",
            playerId: player.id,
            stoneId: stone.id,
            position: { ...stone.position },
            value: stone.value,
            absorbedValue: absorption.absorbedValue,
            overflowValue: absorption.overflowValue,
            overflowStoneValue: absorption.overflowStoneValue,
            overflowStoneCount: absorption.overflowStoneCount,
            totalSpiritStoneCount: player.spiritStoneCount,
            level: player.level,
        });
    }
}
function applySpiritAbsorptionMultiplier(player, stone) {
    const multiplier = player.treasureEffects.spiritAbsorptionMultiplier ?? 1;
    if (!Number.isFinite(multiplier) || multiplier <= 0) {
        return stone.value;
    }
    return Math.max(0, Math.floor(stone.value * multiplier));
}
function isPositionInSpiritStonePickupRange(player, position, config) {
    const distance = distanceBetween(player.position, position);
    if (distance <= MIN_SPIRIT_STONE_PICKUP_RADIUS + EPSILON) {
        return true;
    }
    return isPositionInPlayerArc(player, position, config, distance);
}
function syncPlayerStaminaForLevel(player) {
    const staminaConfig = getStaminaConfigForLevel(player.level);
    player.maxStamina = staminaConfig.maxStamina;
    player.stamina = Math.min(player.stamina, player.maxStamina);
}
function restorePlayerStaminaFromSpiritStone(player, absorbedValue) {
    if (absorbedValue <= 0) {
        return;
    }
    const minRestore = player.maxStamina * PICKUP_STAMINA_RESTORE_MIN_RATIO;
    const maxRestore = player.maxStamina * PICKUP_STAMINA_RESTORE_MAX_RATIO;
    const valueRestore = absorbedValue * PICKUP_STAMINA_RESTORE_PER_SPIRIT;
    const restore = Math.min(maxRestore, Math.max(minRestore, valueRestore));
    player.stamina = Math.min(player.maxStamina, player.stamina + restore);
}
function spawnOverflowSpiritStones(world, player, sourceStone, absorption) {
    if (!absorption.overflowStoneValue || absorption.overflowStoneCount <= 0) {
        return;
    }
    for (let index = 0; index < absorption.overflowStoneCount; index += 1) {
        const position = createDirectionalScatterPosition(world, player, index);
        world.spiritStones[`${sourceStone.id}:overflow:${index.toString()}`] = {
            id: `${sourceStone.id}:overflow:${index.toString()}`,
            position,
            value: absorption.overflowStoneValue,
        };
    }
}
function createDirectionalScatterPosition(world, player, index) {
    const backAngle = player.facing + Math.PI;
    const backDirection = {
        x: Math.cos(backAngle),
        y: Math.sin(backAngle),
    };
    const sideDirection = {
        x: -Math.sin(backAngle),
        y: Math.cos(backAngle),
    };
    const distance = OVERFLOW_SCATTER_START_DISTANCE +
        index * OVERFLOW_SCATTER_STEP_DISTANCE +
        deterministicJitter(player.id, index, 0) * 10;
    const lateral = (deterministicJitter(player.id, index, 1) * 2 - 1) * 18;
    const candidate = {
        x: player.position.x +
            backDirection.x * distance +
            sideDirection.x * lateral,
        y: player.position.y +
            backDirection.y * distance +
            sideDirection.y * lateral,
    };
    return projectSpiritStoneInsideArena(world, candidate, index);
}
function projectSpiritStoneInsideArena(world, position, index) {
    const maxDistance = Math.max(0, world.config.arenaRadius - OVERFLOW_SCATTER_EDGE_MARGIN);
    const distanceFromCenter = distanceToOrigin(position);
    if (distanceFromCenter <= maxDistance || distanceFromCenter === 0) {
        return position;
    }
    const normal = {
        x: position.x / distanceFromCenter,
        y: position.y / distanceFromCenter,
    };
    const tangent = { x: -normal.y, y: normal.x };
    const tangentOffset = ((index % 11) - 5) * 5;
    const projected = {
        x: normal.x * maxDistance + tangent.x * tangentOffset,
        y: normal.y * maxDistance + tangent.y * tangentOffset,
    };
    const projectedDistance = distanceToOrigin(projected);
    if (projectedDistance <= maxDistance || projectedDistance === 0) {
        return projected;
    }
    return {
        x: (projected.x / projectedDistance) * maxDistance,
        y: (projected.y / projectedDistance) * maxDistance,
    };
}
function createSpiritStonePickupArc(player) {
    const attackArc = createAttackArcConfigForPlayer(player);
    return {
        ...attackArc,
        radius: Math.min(attackArc.radius, PICKUP_ARC_RADIUS_CAP),
    };
}
function isPositionInPlayerArc(player, position, config, knownDistance) {
    const distance = knownDistance ?? distanceBetween(player.position, position);
    if (distance > config.radius + EPSILON) {
        return false;
    }
    const angleToPosition = Math.atan2(position.y - player.position.y, position.x - player.position.x);
    return (Math.abs(normalizeAngle(angleToPosition - player.facing)) <=
        config.arcRadians / 2 + EPSILON);
}
function constrainPlayerToArena(world, player) {
    const distanceFromCenter = distanceToOrigin(player.position);
    const maxCenterDistance = Math.max(0, world.config.arenaRadius - player.radius);
    if (distanceFromCenter <= maxCenterDistance) {
        return;
    }
    if (distanceFromCenter === 0) {
        return;
    }
    const normal = {
        x: player.position.x / distanceFromCenter,
        y: player.position.y / distanceFromCenter,
    };
    player.position = {
        x: normal.x * maxCenterDistance,
        y: normal.y * maxCenterDistance,
    };
    const outwardSpeed = player.velocity.x * normal.x + player.velocity.y * normal.y;
    if (outwardSpeed > 0) {
        player.velocity = {
            x: player.velocity.x - normal.x * outwardSpeed,
            y: player.velocity.y - normal.y * outwardSpeed,
        };
    }
}
function resolvePlayerCollisions(world) {
    const players = Object.values(world.players).filter((player) => player.isAlive);
    for (let leftIndex = 0; leftIndex < players.length; leftIndex += 1) {
        for (let rightIndex = leftIndex + 1; rightIndex < players.length; rightIndex += 1) {
            resolvePlayerCollisionPair(world, players[leftIndex], players[rightIndex]);
        }
    }
}
function resolvePlayerCollisionPair(world, left, right) {
    const offset = {
        x: right.position.x - left.position.x,
        y: right.position.y - left.position.y,
    };
    const distance = Math.hypot(offset.x, offset.y);
    const minimumDistance = left.radius + right.radius;
    if (distance >= minimumDistance) {
        return;
    }
    const normal = distance > 0
        ? { x: offset.x / distance, y: offset.y / distance }
        : fallbackCollisionNormal(left.id, right.id);
    const overlap = minimumDistance - distance;
    const correction = Math.min(overlap * PLAYER_COLLISION_PUSH_STRENGTH, PLAYER_COLLISION_MAX_CORRECTION);
    const leftMass = getPlayerCollisionMass(left);
    const rightMass = getPlayerCollisionMass(right);
    const totalMass = leftMass + rightMass;
    const leftCorrection = correction * (rightMass / totalMass);
    const rightCorrection = correction * (leftMass / totalMass);
    left.position = {
        x: left.position.x - normal.x * leftCorrection,
        y: left.position.y - normal.y * leftCorrection,
    };
    right.position = {
        x: right.position.x + normal.x * rightCorrection,
        y: right.position.y + normal.y * rightCorrection,
    };
    constrainPlayerToArena(world, left);
    constrainPlayerToArena(world, right);
}
function getPlayerCollisionMass(player) {
    return 1 + (Math.max(1, player.level) - 1) * PLAYER_COLLISION_LEVEL_MASS_STEP;
}
function fallbackCollisionNormal(leftId, rightId) {
    const angle = hashStringToUnitInterval(`${leftId}:${rightId}`) * Math.PI * 2;
    return {
        x: Math.cos(angle),
        y: Math.sin(angle),
    };
}
function hashStringToUnitInterval(value) {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0) / 0xffffffff;
}
function deterministicJitter(playerId, index, salt) {
    return hashStringToUnitInterval(`${playerId}:overflow:${index.toString()}:${salt.toString()}`);
}
function distanceBetween(left, right) {
    return Math.hypot(left.x - right.x, left.y - right.y);
}
function distanceToOrigin(position) {
    return Math.hypot(position.x, position.y);
}
